// STEP 4-B · 판매자 오늘의 라이브 상품 전용 페이지
// - 기존 endpoint 재사용: GET /public/sellers/{seller_id}/live-products
// - 판매자 스코프 엄격 유지 (다른 셀러 상품 혼입 금지)
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Radio, ShoppingBag } from 'lucide-react';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';

const won = (n) => `${(Number(n) || 0).toLocaleString()}원`;

const SellerPublicLiveProducts = () => {
  const { sellerId } = useParams();
  const navigate = useNavigate();
  const [seller, setSeller] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, [sellerId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setNotFound(false);
      try {
        const [s, list] = await Promise.all([
          api.get(`/sellers/${sellerId}`),
          api.get(`/public/sellers/${sellerId}/live-products`).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setSeller(s.data);
        // 판매자 스코프 강제 (혼입 방지)
        setItems((list.data || []).filter((p) => !p.seller_id || p.seller_id === sellerId));
      } catch (e) { if (e?.response?.status === 404) setNotFound(true); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [sellerId]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#FF3B30]"/></div>;
  if (!seller || notFound) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center" data-testid="seller-live-products-not-found">
        <h2 className="text-xl font-bold mb-2">판매자를 찾을 수 없습니다.</h2>
        <Link to="/" className="text-[#FF3B30]">홈으로</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A]" data-testid="seller-live-products-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <button onClick={() => navigate(`/seller/${sellerId}`)}
          className="inline-flex items-center gap-1 text-xs font-bold text-gray-500 hover:text-[#FF3B30] mb-4"
          data-testid="back-to-seller-profile">
          <ChevronLeft size={14}/> {seller.name} 프로필로
        </button>

        <div className="flex items-center justify-between mb-5">
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight inline-flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Radio className="text-[#FF3B30]" size={22}/> 오늘의 라이브 상품
            <span className="text-sm text-gray-400" data-testid="live-products-count">{items.length}개</span>
          </h1>
        </div>

        {items.length === 0 ? (
          <div className="rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] p-10 text-center" data-testid="live-products-empty">
            <ShoppingBag className="mx-auto text-gray-400 mb-3" size={32}/>
            <p className="text-sm font-bold mb-1">오늘 라이브 편성 상품이 없습니다.</p>
            <p className="text-xs text-gray-500">방송이 편성되면 자동으로 표시됩니다.</p>
            <Link to={`/seller/${sellerId}/products`} className="inline-block mt-4 px-4 py-2 rounded-full bg-[#FF3B30] text-white text-xs font-black" data-testid="empty-goto-all-products">
              판매자 전체 상품 보기
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {items.map((p) => (
              <Link to={`/product/${p.product_id}`} key={p.product_id}
                className="rounded-lg overflow-hidden border border-black/5 dark:border-white/10 bg-white dark:bg-[#141414] hover:-translate-y-0.5 hover:shadow transition-all"
                data-testid={`live-product-card-${p.product_id}`}>
                <div className="relative aspect-square bg-gray-100 overflow-hidden">
                  <SafeImg src={p.image} kind="product" alt={p.name} className="w-full h-full object-cover"/>
                  {p.is_featured && (
                    <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded-full bg-[#FF3B30] text-white text-[9px] font-black">★ 대표</span>
                  )}
                </div>
                <div className="p-2">
                  <p className="text-xs font-medium line-clamp-2 min-h-[2rem]">{p.name}</p>
                  <div className="flex items-baseline gap-1 mt-1">
                    {p.discount_rate > 0 && <span className="text-[#FF3B30] text-[10px] font-black">-{p.discount_rate}%</span>}
                    <span className="text-xs font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>{won(p.sale_price)}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SellerPublicLiveProducts;
